import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, HostBinding } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@se/se.ui.html5';
import EstensaModel, { RadioEprEvent } from 'estensa-module';
import { Subscription, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SeSideComponentType, SeSideComponentBase } from '../se-side-components-models';
import { PatientHistoryService, IEprEventsWithErrorMessage } from '../../../services/patient-history.service';
import {
    seSidePatientHistoryComponentSetPatient,
    seSidePatientHistoryComponentNoResult,
    SeSidePatientHistoryComponentActions,
} from './redux/se-side-patient-history.actions';
import { ImagingService } from '../../../services/imaging/imaging.service';
import { seSearchSelect } from '../../../search-page/redux/search-page.actions';
import { RadioSettingsService } from '../../../services/settings/radio-settings.service';
import { fromServerIsoString } from '../../../models/interop';
import { RootState } from '../../../root-store/root.state';
import { ReportWorkflowService } from '../../../services/report-workflow.service';
import { CancellationError } from '../../../confirm/confirm-dialog.service';
import { PatientSettingsService } from '../../../services/settings/patient-settings.service';

@Component({
    selector: 'se-side-patient-history',
    templateUrl: './se-side-patient-history.component.html',
    styleUrls: ['./se-side-patient-history.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeSidePatientHistoryComponent extends SeSideComponentBase implements OnInit, OnDestroy {
    @HostBinding('style.display')
    isAllBUSearch = false;
    EnableAllBusinessUnitExaminationSearch: boolean;

    type: SeSideComponentType = SeSideComponentType.patientHistory;

    actionsSubscription: Subscription;
    searchSelectSubscription: Subscription;
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    items : IRadioEprEventEx[] = [];
    patientHistoryMessage : string = null;

    // By default chiediamo se si vuole scaricare il viewer dalle immagini precedenti
    reportImageActionType : EstensaModel.ImageActionType = EstensaModel.ImageActionType.Ask;

    private formType;
    private currentPatientId : string;
    private userId : string;


    constructor (
        private translateService : TranslateService,
        private patientHistoryService : PatientHistoryService,
        private actions : Actions,
        private store : Store<RootState>,
        private reportWorkflowService : ReportWorkflowService,
        private notificationService : NotificationService,
        private radioSettingsService : RadioSettingsService,
        private imagingService: ImagingService,
        private patientSettingsService: PatientSettingsService
    ) {
        super();

        this.patientHistoryMessage = this.translateService.instant('NoResults');
        this.isLoading.next(false);
    }

    ngOnInit () {
        this.radioSettingsService
            .radioSettingsAsync
            .subscribe(settings => (this.reportImageActionType = settings.reportSettings.reportImageActionType));
        this.EnableAllBusinessUnitExaminationSearch = this.patientSettingsService.enableAllBusinessUnitExaminationSearch();
    
        this.listenForStoreEvents();

    }

    ngOnDestroy () {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();

        this.actionsSubscription.unsubscribe();
        this.searchSelectSubscription.unsubscribe();

        // Scarico eventuali immagini dal viewer
        this.imagingService.discardImages();

        this.formType = undefined;
    }

    onHistorySelectionChanged (selectedItem : IRadioEprEventEx) {
        this.items.forEach((item) => {
            item.selected = (item.id == selectedItem.id);
        });
    }


    private listenForStoreEvents () {
        this.actionsSubscription = this.actions.pipe(ofType(
            seSidePatientHistoryComponentSetPatient,
            seSidePatientHistoryComponentNoResult)).subscribe(
                async a => {
                    this.isLoading.next(true);
                   
                    
                    this.formType = a.formType;


                    const notAvailableMsg = this.translateService.instant('ClinicalHistoryNotAvailable');
                    const noResultMsg = this.translateService.instant('NoResults');

                    switch (a.type) {
                        case SeSidePatientHistoryComponentActions.SET_PATIENT:
                            if (!a.patientId) {
                                this.patientHistoryMessage = noResultMsg;
                                this.isLoading.next(false);
                                break;
                            }
                            this.currentPatientId = a.patientId;
                            this.patientHistoryMessage = this.translateService.instant('Loading');
                            try {
                                
                                let credentials = a.isAllBuSearch ? await this.reportWorkflowService.askAuthenticationConfirm(this.userId) : null;
                                this.patientHistoryService.getAllEprEvents(a.patientId.toString(), a.isAllBuSearch, credentials)
                                    .pipe(takeUntil(this.ngUnsubscribe)) // blocca le eventuali chiamate in corso
                                    .subscribe({
                                        next:                                        (result: IEprEventsWithErrorMessage) => {
                                            this.items = [];
                                            if (!result.displayMessage && !result.exceptionMessage) {
                                                this.patientHistoryMessage = null;

                                                // Mappo sull'eprEventEx che ha anche le informazioni di selezione er la UI
                                                if (result.eprEvents && result.eprEvents.length > 0) {

                                                    result.eprEvents.forEach((event) => {
                                                        const dateString = this.formatDate(event.date);
                                                        const reportDateString = this.formatDate(event.reportDate);
                                                        
                                                        this.items.push({ ...event, selected: false, reportDateString: reportDateString, dateString: dateString });
                                                    });
                                                }
                                            }
                                            else {
                                                this.patientHistoryMessage = noResultMsg;

                                                // Notifica warning
                                                this.notificationService.warning(result.displayMessage, notAvailableMsg);
                                            }

                                            this.isLoading.next(false);
                                        },
                                        error: err => {
                                            this.patientHistoryMessage = noResultMsg;
                                            
                                            if (err.data == null || err.data.exceptionTypeName !== 'Estensa.Web.Exceptions.CredentialsFault') {
                                                this.items = [];
                                                this.notificationService.warning(
                                                    (err.statusText) ? err.statusText : '', notAvailableMsg);
                                            }
                                            else if (err.data != null && err.data.exceptionTypeName == 'Estensa.Web.Exceptions.CredentialsFault') {

                                                //messaggio della credenziali
                                                this.notificationService.warning(
                                                    (err.data.exceptionMessage) ? err.data.exceptionMessage:'');
                                            }

                                            this.isLoading.next(false);
                                        }
                                    });
                            }

                            catch (error) {
                                if (!(error instanceof CancellationError)) {
                                    this.items = [];
                                    this.notificationService.warning(
                                    (error && error.message) ? error.message : '', notAvailableMsg);
                                }
                                this.patientHistoryMessage = noResultMsg;

                                // Notifica warning
                                this.notificationService.warning(
                                    (error && error.message) ? error.message : '');

                                this.isLoading.next(false);
                            }
                            break;

                        case SeSidePatientHistoryComponentActions.NO_RESULT:
                            this.ngUnsubscribe.next();
                            this.patientHistoryMessage = a.message;
                            this.items = [];
                            this.isLoading.next(false);
                            break;

                        default:
                            this.patientHistoryMessage = noResultMsg;
                            this.isLoading.next(false);
                            break;
                    }
                });

        // Cambio di selezione in lista
        this.searchSelectSubscription = this.actions.pipe(ofType(seSearchSelect)).subscribe(() => {
            // Scarico eventuali immagini dal viewer
            this.imagingService.discardImages();
        });
    }

    private formatDate (date : string | Date | null) {
        if (!date) {
            return null;
        }
        if (date instanceof Date) {
            date = date.toJSON();
        }

        return fromServerIsoString(date)
            .toDate()
            .toLocaleDateString(
                undefined,
                { hour: '2-digit', minute: '2-digit' }
            );
    }
    async reLoadEprEventsRequired () {
        this.isAllBUSearch = true;
        // Chiamare l'azione con i nuovi dati
        this.store.dispatch(
            seSidePatientHistoryComponentSetPatient({
                formType: this.formType,
                patientId: this.currentPatientId,
                isAllBuSearch: this.isAllBUSearch,
            })
        );
    }


}
export interface IRadioEprEventEx extends RadioEprEvent {
    selected : boolean;
    dateString : string;
    reportDateString : string;
}
